import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import { IDataPointsAddFields, newAdditionalFields } from '@/models/AdditionalFields';
import IFieldData from '@/models/forms/IFieldData';
import IFormCodeFields from '@/models/forms/IFormCodeFields';
import IFormFields from '@/models/forms/IFormFields';
import FormsService from '@/services/2.6/FormsService';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import { formFieldsStore, Getters, Mutations, store } from '@/store';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';

export default {
    /**
     * Fetch form fields from the service using the given formCode and put into the store.
     * Replaces any existing form fields in the store for the given formCode.
     * When certain changes to fields are made the form fields must be reloaded.
     * @param formCode
     * @returns the form fields array, empty on an error or unsupported form code
     */
    async fetchAndStoreFormFields(formCode: string): Promise<IFormFields> {
        await fetchAndStore(formCode);
        const formFields = formFieldsStore.FormFields;
        return formFields;
    },

    /**
     * Get form fields from the store for the form code found in local storage.
     * When none are found in the store, will fetch from the service and place into the store.
     * @returns the form fields array, empty on an error or unsupported form code
     */
    async getFormFields(): Promise<IFormFields> {
        const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode) ?? '';
        let formFields = formFieldsStore.FormFields;
        if (formFields?.length > 0) {
            return formFields;
        }

        formFields = await this.fetchAndStoreFormFields(formCode);
        return formFields;
    },

    async getFieldDataById(fieldId: number): Promise<IFieldData | null> {
        const formFields = await this.getFormFields();
        const fieldData = formFields.find((fd) => fd.fieldId === fieldId);
        return fieldData ?? null;
    },

    async getFieldDataByName(fieldName: string): Promise<IFieldData | null> {
        const formFields = await this.getFormFields();
        const fieldData = formFields.find((fd) => fd.fieldName === fieldName);
        return fieldData ?? null;
    },

    async getAdditionalFields(): Promise<IDataPointsAddFields> {
        let additionalFields = formFieldsStore.AdditionalFields;
        if (additionalFields) {
            return additionalFields;
        }

        await fetchAdditionalFields();
        additionalFields = formFieldsStore.AdditionalFields;
        return additionalFields ?? newAdditionalFields();
    },

    async fetchAndStoreAdditionalFields(): Promise<IDataPointsAddFields> {
        await fetchAdditionalFields();
        const additionalFields = formFieldsStore.AdditionalFields;
        return additionalFields ?? newAdditionalFields();
    },
};

async function fetchAndStore(formCode: string) {
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
    let formFields = await new FormsService(webEnvConfig).getDataFields(formCode);
    if (!formFields) {
        formFields = [];
    }

    const formCodeFields: IFormCodeFields = {
        formCode: formCode,
        formFields: formFields,
    };

    formFieldsStore.updateFields(formCodeFields);
}

async function fetchAdditionalFields(): Promise<void> {
    const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode) || '';
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
    let result = await new FormsService(webEnvConfig).getAdditionalFields(formCode);

    if (result.additionalFields.length === 0 && result.dataPoints.length === 0) {
        result = newAdditionalFields();
    }

    formFieldsStore.updateAdditionalFields(result);
}
